/**
 * AvatarPhoto Component
 *
 * A reusable avatar component that displays user profile photos with fallbacks.
 * Supports loading states, error handling, and theme-aware styling.
 *
 * @module components/ui/AvatarPhoto
 */

import React, { useState, useEffect } from 'react';
import { useTheme } from '@/hooks/useTheme';
import { cn } from '@/utils/cn';
import { getLogger } from '@/services/shared/LoggerFactory';

export interface AvatarPhotoProps {
  /** Profile photo URL */
  src?: string | null;
  /** User's first name for initials fallback */
  firstName?: string;
  /** User's last name for initials fallback */
  lastName?: string;
  /** Alt text for the image */
  alt?: string;
  /** Size of the avatar */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Whether the photo is loading */
  loading?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Callback when image fails to load */
  onError?: () => void;
}

const sizeClasses = {
  sm: 'w-8 h-8',
  md: 'w-12 h-12',
  lg: 'w-16 h-16',
  xl: 'w-24 h-24'
};

const textSizeClasses = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-lg',
  xl: 'text-2xl'
};

const iconSizeClasses = {
  sm: 'w-4 h-4',
  md: 'w-6 h-6',
  lg: 'w-8 h-8',
  xl: 'w-12 h-12'
};

export function AvatarPhoto({
  src,
  firstName,
  lastName,
  size = 'xl',
  loading = false,
  className,
  onError
}: AvatarPhotoProps) {
  const logger = getLogger();
  const { isDark } = useTheme();
  const [imageUrl, setImageUrl] = useState<string>('');
  const [imageError, setImageError] = useState<boolean>(false);
  const [isLoadingImage, setIsLoadingImage] = useState<boolean>(false);

  useEffect(() => {
    const loadImage = async () => {
      if (!src) {
        setImageUrl('');
        setImageError(false);
        return;
      }

      setIsLoadingImage(true);
      setImageError(false);

      try {
        // Use src as regular URL
        setImageUrl(src);
      } catch (error) {
        logger.error('Error loading image:', error);
        setImageError(true);
        setImageUrl('');
      } finally {
        setIsLoadingImage(false);
      }
    };

    loadImage();
  }, [src]);

  const handleImageError = () => {
    setImageError(true);
    setImageUrl('');
    onError?.();
  };

  // Generate initials from first and last name
  const getInitials = () => {
    if (!firstName) return '';
    const firstInitial = firstName.charAt(0).toUpperCase();
    const lastInitial = lastName?.charAt(0).toUpperCase() || '';
    return `${firstInitial}${lastInitial}`;
  };

  // Generate alt text
  const getAltText = () => {
    return '';
  };

  return (
    <div className={cn('relative', className)}>
      <div className={cn(
        sizeClasses[size],
        'rounded-full flex items-center justify-center overflow-hidden',
        isDark ? 'bg-secondary-700' : 'bg-secondary-200'
      )}>
        {loading || isLoadingImage ? (
          <div className={cn(
            'animate-pulse bg-neutral-300 rounded-full',
            sizeClasses[size]
          )} />
        ) : imageUrl && !imageError ? (
          <img
            src={imageUrl}
            alt={getAltText()}
            className="w-full h-full object-cover rounded-full"
            onError={handleImageError}
          />
        ) : firstName ? (
          <span className={cn(
            textSizeClasses[size],
            'font-semibold text-center',
            isDark ? 'text-neutral-300' : 'text-secondary-600'
          )}>
            {getInitials()}
          </span>
        ) : (
          <svg
            className={cn(
              iconSizeClasses[size],
              isDark ? 'text-neutral-400' : 'text-neutral-500'
            )}
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
          </svg>
        )}
      </div>
    </div>
  );
}

export default AvatarPhoto;